import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Clock, Users, MapPin, Mountain, ChevronLeft, Check, Minus, Plus, Tag, Percent, Baby } from "lucide-react";
import { format } from "date-fns";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { getImageUrl } from "@/lib/api";
import { ReviewSection } from "@/components/ReviewSection";
import { TourRecommendations } from "@/components/TourRecommendations";
import { useCurrency } from "@/lib/CurrencyContext";
import { useSEO } from "@/lib/seo";

export default function TourDetailPage() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  const [tour, setTour] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [booking, setBooking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dynamicPrice, setDynamicPrice] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [codeValid, setCodeValid] = useState(null);
  const [adults, setAdults] = useState(1);
  const [childCount, setChildCount] = useState(0);

  const selectedDateStr = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;

  useSEO(tour ? {
    title: `${tour.title} - ${tour.category} Tour in Saint Lucia`,
    description: `${tour.description} From $${tour.price}/person. ${tour.duration}, ${tour.difficulty} difficulty. Book this top-rated Saint Lucia ${tour.category.toLowerCase()} tour online with Keyz's Epic Adventure Tours.`,
    image: tour.images?.[0]?.startsWith("http") ? tour.images[0] : undefined,
    type: "product",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      "name": tour.title,
      "description": tour.description,
      "touristType": tour.category,
      "offers": { "@type": "Offer", "price": tour.price, "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
      "provider": { "@type": "Organization", "name": "Keyz's Epic Adventure Tours", "url": "https://kzepictours.com" }
    }
  } : {});

  useEffect(() => {
    Promise.all([
      api.get(`/tours/${tourId}`),
      api.get(`/tours/${tourId}/availability`)
    ]).then(([tourRes, availRes]) => {
      setTour(tourRes.data);
      setAvailability(availRes.data);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      toast.error("Tour not found");
    });
  }, [tourId]);

  // Fetch dynamic price when date changes
  useEffect(() => {
    if (!selectedDateStr || !tour) return;
    api.get(`/tours/${tourId}/price`, { params: { date: selectedDateStr } })
      .then(res => setDynamicPrice(res.data))
      .catch(() => setDynamicPrice(null));
  }, [selectedDateStr, tourId, tour]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bone">
        <Navbar />
        <div className="pt-28 px-6 max-w-7xl mx-auto animate-pulse">
          <div className="h-96 bg-midnight/5 mb-8" />
          <div className="h-8 bg-midnight/5 w-1/2 mb-4" />
          <div className="h-4 bg-midnight/5 w-full mb-2" />
          <div className="h-4 bg-midnight/5 w-3/4" />
        </div>
      </div>
    );
  }

  if (!tour) return null;

  const getAvailableSpots = (dateStr) => {
    if (availability?.dates?.[dateStr]) {
      return availability.dates[dateStr].available;
    }
    return availability?.default_max_capacity || 20;
  };

  const isDateDisabled = (date) => {
    if (date < new Date()) return true;
    const dateStr = format(date, "yyyy-MM-dd");
    return getAvailableSpots(dateStr) <= 0;
  };

  const spotsLeft = selectedDateStr ? getAvailableSpots(selectedDateStr) : null;

  const unitPrice = dynamicPrice?.final_price || tour?.price || 0;
  const childPrice = tour?.child_price || 0;
  const childrenAllowed = tour?.children_allowed !== false;
  const adultTotal = unitPrice * adults;
  const childTotal = childPrice * childCount;
  const subtotal = adultTotal + childTotal;
  const isFamily = adults > 0 && childCount > 0;
  const isGroup = (adults + childCount) >= 10;
  const bundleDiscount = isGroup ? 0.15 : (isFamily ? 0.10 : 0);
  const bundleLabel = isGroup ? "Group Booking" : (isFamily ? "Family Package" : null);
  const totalPrice = subtotal * (1 - bundleDiscount);
  const totalGuests = adults + childCount;
  const hasDiscount = dynamicPrice && dynamicPrice.discount_percent > 0;

  const handleBooking = async () => {
    if (!customerName || !customerEmail) {
      toast.error("Please fill in your name and email");
      return;
    }
    setBooking(true);
    try {
      const res = await api.post("/bookings", {
        tour_id: tour.tour_id,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        date: selectedDateStr,
        adults: adults,
        children: childCount,
        discount_code: discountCode || "",
      });
      toast.success("Booking confirmed!");
      setCheckoutOpen(false);
      navigate("/booking-confirmation", { state: { booking: res.data, tour } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  const difficultyColor = {
    Easy: "bg-canopy/10 text-canopy",
    Moderate: "bg-sandy/20 text-sandy",
    Hard: "bg-red-100 text-red-700",
  };

  return (
    <PayPalScriptProvider options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
    <div className="min-h-screen bg-bone">
      <Navbar />

      <div className="pt-24 px-6 max-w-7xl mx-auto pb-24">
        {/* Back Link */}
        <Link to="/tours" className="inline-flex items-center gap-1 text-midnight/50 font-manrope text-sm mb-8 hover:text-sandy transition-colors duration-200" data-testid="back-to-tours">
          <ChevronLeft className="w-4 h-4" /> Back to Tours
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left: Images + Details */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="mb-8" data-testid="tour-image-gallery">
              <div className="overflow-hidden aspect-[16/10] mb-3">
                <img
                  src={getImageUrl(tour.images?.[selectedImage])}
                  alt={tour.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {tour.images?.length > 1 && (
                <div className="flex gap-3">
                  {tour.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`w-20 h-16 overflow-hidden border-2 transition-colors duration-200 ${i === selectedImage ? 'border-sandy' : 'border-transparent'}`}
                      data-testid={`thumbnail-${i}`}
                    >
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Tour Info */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="font-manrope text-xs uppercase tracking-wider border-sandy/30 text-sandy">
                  {tour.category}
                </Badge>
                <Badge className={`font-manrope text-xs ${difficultyColor[tour.difficulty] || ''}`}>
                  {tour.difficulty}
                </Badge>
              </div>
              <h1 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-light text-midnight mb-4" data-testid="tour-title">
                {tour.title}
              </h1>
              <div className="flex flex-wrap gap-6 mb-6">
                <span className="flex items-center gap-2 font-manrope text-sm text-midnight/60">
                  <Clock className="w-4 h-4 text-sandy" /> {tour.duration}
                </span>
                <span className="flex items-center gap-2 font-manrope text-sm text-midnight/60">
                  <Users className="w-4 h-4 text-sandy" /> Max {tour.max_capacity} guests
                </span>
                <span className="flex items-center gap-2 font-manrope text-sm text-midnight/60">
                  <MapPin className="w-4 h-4 text-sandy" /> {tour.meeting_point}
                </span>
              </div>
              <p className="font-manrope text-base text-midnight/70 leading-relaxed mb-8">
                {tour.long_description || tour.description}
              </p>
            </div>

            {/* What's Included */}
            {tour.whats_included?.length > 0 && (
              <div className="mb-8" data-testid="whats-included-section">
                <h3 className="font-cormorant text-2xl font-light text-midnight mb-4">What's Included</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tour.whats_included.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-canopy mt-0.5 flex-shrink-0" />
                      <span className="font-manrope text-sm text-midnight/70">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Highlights */}
            {tour.highlights?.length > 0 && (
              <div data-testid="highlights-section">
                <h3 className="font-cormorant text-2xl font-light text-midnight mb-4">Highlights</h3>
                <div className="flex flex-wrap gap-3">
                  {tour.highlights.map((h, i) => (
                    <Badge key={i} variant="outline" className="font-manrope text-xs py-1.5 px-3 border-midnight/10">
                      {h}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            <ReviewSection tourId={tourId} />

            {/* Recommendations */}
            <TourRecommendations tourId={tourId} />
          </div>

          {/* Right: Booking Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white p-6 border border-midnight/5" data-testid="booking-panel">
              <div className="mb-6">
                <p className="font-manrope text-xs uppercase tracking-widest text-midnight/40 mb-1">Starting at</p>
                {hasDiscount ? (
                  <div>
                    <div className="flex items-baseline gap-2">
                      <p className="font-cormorant text-4xl font-light text-midnight">
                        {formatPrice(unitPrice)}<span className="text-lg text-midnight/40">/person</span>
                      </p>
                      <p className="font-manrope text-sm line-through text-midnight/30">{formatPrice(tour.price)}</p>
                    </div>
                    <Badge className="mt-1 bg-canopy/10 text-canopy font-manrope text-[10px]" data-testid="discount-badge">
                      <Percent className="w-2.5 h-2.5 mr-0.5" />
                      {dynamicPrice.discount_type === "early_bird" ? "Early Bird" : "Last Minute"} -{dynamicPrice.discount_percent}%
                    </Badge>
                  </div>
                ) : (
                  <p className="font-cormorant text-4xl font-light text-midnight">
                    {formatPrice(tour.price)}<span className="text-lg text-midnight/40">/person</span>
                  </p>
                )}
              </div>

              <Separator className="mb-6" />

              {/* Calendar */}
              <div className="mb-6">
                <p className="font-manrope text-xs uppercase tracking-widest text-midnight/40 mb-3">Select Date</p>
                <div className="booking-calendar">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={isDateDisabled}
                    modifiers={{
                      limited: (date) => {
                        if (date < new Date()) return false;
                        const dateStr = format(date, "yyyy-MM-dd");
                        const spots = getAvailableSpots(dateStr);
                        const max = availability?.default_max_capacity || 20;
                        return spots > 0 && spots <= Math.ceil(max * 0.3);
                      },
                      available: (date) => {
                        if (date < new Date()) return false;
                        const dateStr = format(date, "yyyy-MM-dd");
                        const spots = getAvailableSpots(dateStr);
                        const max = availability?.default_max_capacity || 20;
                        return spots > Math.ceil(max * 0.3);
                      },
                    }}
                    modifiersStyles={{
                      limited: { backgroundColor: 'rgba(197, 160, 89, 0.15)', color: '#92750A', fontWeight: '600' },
                      available: { backgroundColor: 'rgba(6, 95, 70, 0.08)', color: '#065F46' },
                    }}
                    className="border border-midnight/5"
                    data-testid="booking-calendar"
                  />
                </div>
                {/* Calendar Legend */}
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(6, 95, 70, 0.15)' }} />
                    <span className="font-manrope text-[10px] text-midnight/40">Available</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(197, 160, 89, 0.25)' }} />
                    <span className="font-manrope text-[10px] text-midnight/40">Limited</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-midnight/10" />
                    <span className="font-manrope text-[10px] text-midnight/40">Fully Booked</span>
                  </div>
                </div>
                {selectedDate && spotsLeft !== null && (
                  <p className={`mt-2 font-manrope text-xs ${spotsLeft <= 3 ? 'text-sandy font-semibold' : 'text-canopy'}`} data-testid="spots-remaining">
                    {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} remaining for {format(selectedDate, "MMM d, yyyy")}
                  </p>
                )}
              </div>

              {/* Child Pricing Info */}
              {childrenAllowed && childPrice > 0 && (
                <div className="mb-4 p-3 bg-bone border border-sandy/10" data-testid="child-pricing-info">
                  <div className="flex items-center gap-2 mb-1">
                    <Baby className="w-3.5 h-3.5 text-sandy" />
                    <span className="font-manrope text-xs font-medium text-midnight">Children Welcome</span>
                  </div>
                  <p className="font-manrope text-xs text-midnight/50">Child price: {formatPrice(childPrice)}/child</p>
                </div>
              )}

              <Separator className="mb-6" />

              {/* Adults */}
              <div className="mb-4">
                <p className="font-manrope text-xs uppercase tracking-widest text-midnight/40 mb-3">Adults</p>
                <div className="flex items-center gap-4">
                  <button
                    data-testid="decrease-adults-btn"
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                    className="w-8 h-8 flex items-center justify-center border border-midnight/10 text-midnight hover:border-sandy transition-colors duration-200"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-manrope text-lg text-midnight w-8 text-center" data-testid="adult-count">{adults}</span>
                  <button
                    data-testid="increase-adults-btn"
                    onClick={() => setAdults(Math.min(spotsLeft || tour.max_capacity, adults + 1))}
                    className="w-8 h-8 flex items-center justify-center border border-midnight/10 text-midnight hover:border-sandy transition-colors duration-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <span className="font-manrope text-xs text-midnight/40 ml-2">{formatPrice(unitPrice)} each</span>
                </div>
              </div>

              {/* Children */}
              {childrenAllowed && (
                <div className="mb-6">
                  <p className="font-manrope text-xs uppercase tracking-widest text-midnight/40 mb-3">Children</p>
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="decrease-children-btn"
                      onClick={() => setChildCount(Math.max(0, childCount - 1))}
                      className="w-8 h-8 flex items-center justify-center border border-midnight/10 text-midnight hover:border-sandy transition-colors duration-200"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="font-manrope text-lg text-midnight w-8 text-center" data-testid="child-count">{childCount}</span>
                    <button
                      data-testid="increase-children-btn"
                      onClick={() => setChildCount(Math.min((spotsLeft || tour.max_capacity) - adults, childCount + 1))}
                      className="w-8 h-8 flex items-center justify-center border border-midnight/10 text-midnight hover:border-sandy transition-colors duration-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <span className="font-manrope text-xs text-midnight/40 ml-2">{childPrice > 0 ? `${formatPrice(childPrice)} each` : 'Free'}</span>
                  </div>
                </div>
              )}

              <Separator className="mb-4" />

              {/* Price Breakdown */}
              <div className="space-y-2 mb-4" data-testid="price-breakdown">
                <div className="flex justify-between font-manrope text-xs text-midnight/50">
                  <span>{adults} adult{adults !== 1 ? 's' : ''} x {formatPrice(unitPrice)}</span>
                  <span>{formatPrice(adultTotal)}</span>
                </div>
                {childCount > 0 && (
                  <div className="flex justify-between font-manrope text-xs text-midnight/50">
                    <span>{childCount} child{childCount !== 1 ? 'ren' : ''} x {formatPrice(childPrice)}</span>
                    <span>{formatPrice(childTotal)}</span>
                  </div>
                )}
                {isFamily && !isGroup && (
                  <div className="flex justify-between font-manrope text-xs text-canopy">
                    <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> Family Package -10%</span>
                    <span>-{formatPrice(subtotal * 0.10)}</span>
                  </div>
                )}
                {isGroup && (
                  <div className="flex justify-between font-manrope text-xs text-canopy">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Group Booking -15%</span>
                    <span>-{formatPrice(subtotal * 0.15)}</span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="flex items-center justify-between mb-2">
                <span className="font-manrope text-sm text-midnight/60">Total</span>
                <span className="font-cormorant text-2xl text-midnight" data-testid="total-price">{formatPrice(totalPrice)}</span>
              </div>
              {bundleLabel && (
                <Badge className="mb-4 bg-canopy/10 text-canopy font-manrope text-[10px]" data-testid="bundle-discount-badge">
                  <Tag className="w-2.5 h-2.5 mr-0.5" /> {bundleLabel} Applied - {isGroup ? '15%' : '10%'} Savings
                </Badge>
              )}

              {/* Book Now */}
              <Button
                data-testid="book-now-btn"
                disabled={!selectedDate}
                onClick={() => setCheckoutOpen(true)}
                className="w-full rounded-full bg-sandy text-midnight hover:bg-sandy/90 h-12 font-manrope font-medium text-sm tracking-wide transition-transform duration-300 hover:-translate-y-0.5"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Dialog (Mock PayPal) */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-cormorant text-2xl font-light text-midnight">Complete Your Booking</DialogTitle>
            <DialogDescription className="font-manrope text-sm text-midnight/50">Fill in your details to confirm your adventure booking.</DialogDescription>
          </DialogHeader>

          <div className="bg-bone p-4 mb-4">
            <p className="font-manrope text-sm font-medium text-midnight">{tour.title}</p>
            <p className="font-manrope text-xs text-midnight/50 mt-1">
              {selectedDate && format(selectedDate, "MMMM d, yyyy")} &middot; {adults} adult{adults !== 1 ? 's' : ''}{childCount > 0 ? ` + ${childCount} child${childCount !== 1 ? 'ren' : ''}` : ''}
              {bundleLabel && <span className="text-canopy ml-1">({bundleLabel})</span>}
            </p>
            <p className="font-cormorant text-xl text-midnight mt-2">{formatPrice(totalPrice)}</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Full Name</Label>
              <Input
                data-testid="checkout-name-input"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter your name"
                className="mt-1 font-manrope text-sm"
              />
            </div>
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Email</Label>
              <Input
                data-testid="checkout-email-input"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="your@email.com"
                type="email"
                className="mt-1 font-manrope text-sm"
              />
            </div>
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Phone (Optional)</Label>
              <Input
                data-testid="checkout-phone-input"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="+1 (234) 567-8900"
                className="mt-1 font-manrope text-sm"
              />
            </div>
            <div>
              <Label className="font-manrope text-xs uppercase tracking-widest text-midnight/50">Discount Code (Optional)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  data-testid="checkout-discount-input"
                  value={discountCode}
                  onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setCodeValid(null); }}
                  placeholder="e.g. EXPLORE..."
                  className="font-manrope text-sm"
                />
                <Button
                  data-testid="checkout-validate-code-btn"
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!discountCode) return;
                    try {
                      const res = await api.post("/validate-discount", { code: discountCode });
                      setCodeValid(res.data);
                      toast.success(`Code valid! ${res.data.discount_percent}% discount`);
                    } catch {
                      setCodeValid(false);
                      toast.error("Invalid code");
                    }
                  }}
                  className="font-manrope text-xs shrink-0"
                >
                  Apply
                </Button>
              </div>
              {codeValid && codeValid.valid && <p className="font-manrope text-[10px] text-canopy mt-1">{codeValid.discount_percent}% {codeValid.type === 'referral' ? 'referral' : ''} discount will be applied{codeValid.referred_by ? ` (from ${codeValid.referred_by})` : ''}</p>}
              {codeValid === false && <p className="font-manrope text-[10px] text-red-500 mt-1">Invalid or expired code</p>}
            </div>
          </div>

          <Button
            data-testid="checkout-submit-btn"
            onClick={handleBooking}
            disabled={booking}
            className="w-full mt-4 paypal-btn rounded-full h-12 text-midnight font-manrope font-semibold text-sm"
          >
            {booking ? "Processing..." : "Pay with Mock Checkout"}
          </Button>

          {/* PayPal Button */}
          <div className="mt-3" data-testid="paypal-buttons-container">
            <PayPalButtons
              style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 45 }}
              disabled={!customerName || !customerEmail}
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [{
                    description: `${tour.title} - ${selectedDateStr} (${adults} adults${childCount > 0 ? `, ${childCount} children` : ''})`,
                    amount: {
                      currency_code: "USD",
                      value: totalPrice.toFixed(2),
                    },
                  }],
                });
              }}
              onApprove={async (data, actions) => {
                try {
                  const details = await actions.order.capture();
                  if (details.status === "COMPLETED") {
                    const res = await api.post("/paypal/complete-booking", {
                      paypal_order_id: data.orderID,
                      tour_id: tour.tour_id,
                      date: selectedDateStr,
                      adults, children: childCount,
                      customer_name: customerName,
                      customer_email: customerEmail,
                      customer_phone: customerPhone,
                      discount_code: discountCode,
                      total_paid: parseFloat(details.purchase_units[0].payments.captures[0].amount.value),
                    });
                    toast.success("Payment successful!");
                    setCheckoutOpen(false);
                    navigate("/booking-confirmation", { state: { booking: res.data, tour } });
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Payment failed. Please try again.");
                }
              }}
              onError={(err) => {
                console.error("PayPal error:", err);
                toast.error("PayPal encountered an error. Please try the mock checkout or contact us.");
              }}
            />
          </div>

          <p className="text-center font-manrope text-[10px] text-midnight/30 mt-2">
            Secure payment powered by PayPal. Mock checkout also available above.
          </p>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
    </PayPalScriptProvider>
  );
}
